import { useState } from "react";
import { toast } from "sonner";
import { Activity, CheckCircle2, XCircle, Loader2, RefreshCw, Trash2, Radio, PlugZap, Clock, FlaskConical, ScanSearch } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useData } from "@/context/DataContext";
import { botStatusApi, demoTools, apiError } from "@/lib/api";
import { InitialSyncDialog } from "@/components/InitialSync";

const ago = (iso) => {
  if (!iso) return "—";
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "adesso";
  if (s < 3600) return `${Math.round(s / 60)} min fa`;
  if (s < 86400) return `${Math.round(s / 3600)} ore fa`;
  return new Date(iso).toLocaleString("it-IT");
};

// Riassunto dello stato: colore, testo breve e spiegazione.
export function statusInfo(st, live) {
  if (!st) return { tone: "#949BA4", short: "Verifica…", long: "Sto controllando il bot." };
  if (!st.reachable) return { tone: "#F23F43", short: "Bot offline", long: "Il sito non riesce a contattare il bot. Le modifiche vengono messe in coda." };
  if (!st.ready) return { tone: "#FEE75C", short: "Discord scollegato", long: "Il bot è acceso ma non è collegato a Discord. Premi «Riconnetti»." };
  if (st.pending) return { tone: "#FEE75C", short: `${st.pending} in coda`, long: "Ci sono modifiche in attesa di essere applicate." };
  return { tone: "#23A55A", short: st.ping != null ? `Online · ${st.ping} ms` : "Online", long: live ? "Tutto collegato: sito, bot e Discord sono sincronizzati in tempo reale." : "Il bot è online. Aggiornamenti in tempo reale in riconnessione…" };
}

export function BotStatusDialog({ open, onOpenChange }) {
  const { botStatus: st, refreshStatus, setBotStatus, live, refresh } = useData();
  const [busy, setBusy] = useState(false);
  const [steps, setSteps] = useState(null);
  const [initialOpen, setInitialOpen] = useState(false);
  const info = statusInfo(st, live);

  const reconnect = async () => {
    setBusy(true); setSteps(null);
    try {
      const r = await botStatusApi.reconnect();
      setSteps(r.steps); setBotStatus(r.status);
      if (r.ok) { toast.success("Bot riconnesso e dati aggiornati"); refresh(); } else toast.error("La riconnessione non è riuscita del tutto");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const removeOp = async (id) => { await botStatusApi.removePending(id); refreshStatus(); };
  const clearOps = async () => { await botStatusApi.clearPending(); refreshStatus(); toast.success("Coda svuotata"); };
  const setOffline = async (v) => { try { await demoTools.offline(v); await refreshStatus(); } catch (e) { toast.error(apiError(e)); } };
  const simulate = async () => { try { await demoTools.discordChange(); } catch (e) { toast.error(apiError(e)); } };

  const Row = ({ label, children }) => (
    <div className="flex items-center justify-between gap-3 border-b border-[#232838] py-2 text-sm last:border-0">
      <span className="text-[#949BA4]">{label}</span><span className="text-right font-medium text-white">{children}</span>
    </div>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[88vh] overflow-y-auto border-[#232838] bg-[#161923] text-[#DBDEE1] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white"><Activity className="h-5 w-5 text-[#8ea0ff]" />Stato del bot</DialogTitle>
            <DialogDescription className="text-[#949BA4]">Collegamento tra sito, bot e Discord.</DialogDescription>
          </DialogHeader>

          <div className="flex items-start gap-3 rounded-xl border p-3" style={{ borderColor: `${info.tone}55`, background: `${info.tone}14` }}>
            <span className="mt-1 h-3 w-3 shrink-0 rounded-full" style={{ background: info.tone, boxShadow: `0 0 10px ${info.tone}` }} />
            <div><p className="font-bold text-white">{info.short}</p><p className="text-sm text-[#C9CDD4]">{info.long}</p></div>
          </div>

          <div className="rounded-xl border border-[#232838] bg-[#131620] px-3">
            <Row label="Modalità">{st?.mode === "demo" ? "Demo (senza bot reale)" : "Bot reale"}</Row>
            <Row label="Connessione a Discord">{st?.connection || (st?.reachable ? "—" : "non raggiungibile")}</Row>
            <Row label="Ping">{st?.ping != null ? `${st.ping} ms` : "—"}</Row>
            {st?.user?.tag && <Row label="Bot">{st.user.tag}</Row>}
            {st?.guild?.name && <Row label="Server">{st.guild.name}</Row>}
            <Row label="Aggiornamenti in tempo reale"><span className="inline-flex items-center gap-1.5"><Radio className={`h-3.5 w-3.5 ${live ? "text-[#23A55A]" : "text-[#F23F43]"}`} />{live ? "attivi" : "non collegati"}</span></Row>
            <Row label="Ultimo evento da Discord">{st?.lastEvent ? `${st.lastEvent.event} · ${ago(st.lastEvent.receivedAt)}` : "nessuno"}</Row>
            {st?.mode !== "demo" && !st?.webhookConfigured && <Row label="Webhook"><span className="text-[#FEE75C]">WEBHOOK_SECRET non impostato</span></Row>}
            {st?.error && <Row label="Errore"><span className="text-[#ff7a7d]">{st.error}</span></Row>}
          </div>

          {st?.pending > 0 && (
            <div className="space-y-2 rounded-xl border border-[#FEE75C]/25 bg-[#FEE75C]/[0.06] p-3">
              <div className="flex items-center justify-between">
                <p className="flex items-center gap-2 text-sm font-bold text-[#FEE75C]"><Clock className="h-4 w-4" />{st.pending} modifiche in coda</p>
                <button onClick={clearOps} className="text-xs font-semibold text-[#ff7a7d] hover:underline">Scarta tutte</button>
              </div>
              <p className="text-xs text-[#C9CDD4]">Vengono applicate da sole appena il bot torna online (controllo ogni 15 secondi), oppure subito con «Riconnetti».</p>
              {(st.pendingOps || []).map((op) => (
                <div key={op.id} className="flex items-center gap-2 rounded-lg bg-[#0f111a] px-2.5 py-1.5 text-xs">
                  <span className="font-mono text-[#8ea0ff]">{op.summary}</span>
                  <span className="ml-auto text-[#949BA4]">{op.by} · {ago(op.createdAt)}</span>
                  <button onClick={() => removeOp(op.id)} title="Scarta" className="text-[#949BA4] hover:text-[#F23F43]"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}

          {steps && (
            <div className="space-y-1.5 rounded-xl border border-[#232838] bg-[#131620] p-3" data-testid="reconnect-steps">
              {steps.map((s, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  {s.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#23A55A]" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#F23F43]" />}
                  <div><p className="text-white">{s.label}</p>{s.detail && <p className="text-xs text-[#949BA4]">{s.detail}</p>}</div>
                </div>
              ))}
            </div>
          )}

          <div className="grid gap-2 sm:grid-cols-2">
            <Button onClick={reconnect} disabled={busy} className="bg-[#5865F2] font-semibold hover:bg-[#4752C4]" data-testid="bot-reconnect-btn">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlugZap className="mr-2 h-4 w-4" />}Riconnetti e riprova
            </Button>
            <Button variant="outline" onClick={() => setInitialOpen(true)} className="border-[#232838] bg-transparent text-[#B5BAC1] hover:bg-[#252B3D] hover:text-white">
              <ScanSearch className="mr-2 h-4 w-4" />Riscansiona il server
            </Button>
          </div>
          <p className="text-xs text-[#949BA4]">«Riconnetti» ricollega il bot a Discord se la connessione è caduta, applica le modifiche in coda e ricarica ruoli, canali e membri.</p>

          {st?.mode === "demo" && (
            <div className="space-y-2 rounded-xl border border-dashed border-[#8B5CF6]/40 p-3">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[#b9a4fb]"><FlaskConical className="h-3.5 w-3.5" />Prove (solo modalità demo)</p>
              <label className="flex items-center justify-between gap-3 text-sm text-[#DBDEE1]">Simula bot spento<Switch checked={!st.reachable} onCheckedChange={setOffline} data-testid="demo-offline-switch" /></label>
              <Button size="sm" variant="outline" onClick={simulate} className="w-full border-[#232838] bg-transparent text-[#B5BAC1] hover:bg-[#252B3D] hover:text-white" data-testid="demo-discord-change">
                <RefreshCw className="mr-2 h-4 w-4" />Simula una modifica fatta su Discord
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <InitialSyncDialog open={initialOpen} onOpenChange={setInitialOpen} />
    </>
  );
}

// Pulsante compatto con pallino colorato: apre la finestra dello stato.
export function BotStatusButton({ className = "", compact = false }) {
  const { botStatus, live } = useData();
  const [open, setOpen] = useState(false);
  const info = statusInfo(botStatus, live);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="bot-status-btn" title={info.long}
        className={`flex items-center gap-2 rounded-lg border border-[#232838] bg-transparent px-3 text-sm font-medium text-[#B5BAC1] transition-colors hover:bg-[#252B3D] hover:text-white ${compact ? "h-9" : "h-9"} ${className}`}>
        <span className="relative flex h-2.5 w-2.5">
          {info.tone === "#23A55A" && live && <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" style={{ background: info.tone }} />}
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full" style={{ background: info.tone }} />
        </span>
        {info.short}
      </button>
      <BotStatusDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
